/**
 * Platform and Content Type Configuration
 * إعدادات المنصات وأنواع المحتوى
 */

// =====================================================
// PLATFORMS - المنصات
// =====================================================
export const PLATFORMS = {
  instagram: {
    id: 'instagram',
    name: 'Instagram',
    nameAr: 'انستقرام',
    icon: '📸',
    color: '#E4405F',
    bgColor: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400',
    contentTypes: ['image_post', 'reel', 'story', 'carousel'],
  },
  tiktok: {
    id: 'tiktok',
    name: 'TikTok',
    nameAr: 'تيك توك',
    icon: '🎵',
    color: '#000000',
    bgColor: 'bg-black',
    contentTypes: ['video_post', 'reel'],
  },
  snapchat: {
    id: 'snapchat',
    name: 'Snapchat',
    nameAr: 'سناب شات',
    icon: '👻',
    color: '#FFFC00',
    bgColor: 'bg-yellow-400',
    contentTypes: ['video_post', 'story'],
  },
  twitter: {
    id: 'twitter',
    name: 'X (Twitter)',
    nameAr: 'إكس (تويتر)',
    icon: '𝕏',
    color: '#000000',
    bgColor: 'bg-black',
    contentTypes: ['image_post', 'video_post', 'header'],
  },
  facebook: {
    id: 'facebook',
    name: 'Facebook',
    nameAr: 'فيسبوك',
    icon: '📘',
    color: '#1877F2',
    bgColor: 'bg-blue-600',
    contentTypes: ['image_post', 'video_post', 'story', 'cover', 'carousel'],
  },
  linkedin: {
    id: 'linkedin',
    name: 'LinkedIn',
    nameAr: 'لينكد إن',
    icon: '💼',
    color: '#0A66C2',
    bgColor: 'bg-blue-700',
    contentTypes: ['image_post', 'video_post', 'banner', 'carousel'],
  },
  website: {
    id: 'website',
    name: 'Website',
    nameAr: 'موقع إلكتروني',
    icon: '🌐',
    color: '#6366F1',
    bgColor: 'bg-indigo-500',
    contentTypes: ['banner', 'hero', 'thumbnail', 'header', 'product', 'blog'],
  },
  other: {
    id: 'other',
    name: 'Other',
    nameAr: 'أخرى',
    icon: '📋',
    color: '#6B7280',
    bgColor: 'bg-gray-500',
    contentTypes: ['image_post', 'video_post', 'banner'],
  },
};

// =====================================================
// CONTENT TYPES - أنواع المحتوى
// =====================================================
export const CONTENT_TYPES = {
  image_post: {
    id: 'image_post',
    name: 'Image Post',
    nameAr: 'بوست صورة',
    icon: '🖼️',
    isVideo: false,
    description: 'Single image post',
    descriptionAr: 'منشور صورة واحدة',
  },
  video_post: {
    id: 'video_post',
    name: 'Video Post',
    nameAr: 'بوست فيديو',
    icon: '🎬',
    isVideo: true,
    description: 'Video content',
    descriptionAr: 'محتوى فيديو',
  },
  reel: {
    id: 'reel',
    name: 'Reel / Short Video',
    nameAr: 'ريل / فيديو قصير',
    icon: '🎞️',
    isVideo: true,
    description: 'Short-form vertical video',
    descriptionAr: 'فيديو قصير عمودي',
  },
  story: {
    id: 'story',
    name: 'Story',
    nameAr: 'ستوري',
    icon: '⭕',
    isVideo: true, // Can be image or video
    description: '24-hour temporary content',
    descriptionAr: 'محتوى مؤقت 24 ساعة',
  },
  carousel: {
    id: 'carousel',
    name: 'Carousel',
    nameAr: 'كاروسيل (عدة صور)',
    icon: '🎠',
    isVideo: false,
    description: 'Multiple images/videos',
    descriptionAr: 'عدة صور أو فيديوهات',
  },
  banner: {
    id: 'banner',
    name: 'Banner',
    nameAr: 'بانر',
    icon: '🏷️',
    isVideo: false,
    description: 'Website or blog banner',
    descriptionAr: 'بانر موقع أو مدونة',
  },
  hero: {
    id: 'hero',
    name: 'Hero Image',
    nameAr: 'صورة رئيسية',
    icon: '🦸',
    isVideo: false,
    description: 'Large hero section image',
    descriptionAr: 'صورة القسم الرئيسي',
  },
  cover: {
    id: 'cover',
    name: 'Cover Photo',
    nameAr: 'صورة الغلاف',
    icon: '📷',
    isVideo: false,
    description: 'Profile or page cover',
    descriptionAr: 'غلاف الصفحة أو الحساب',
  },
  header: {
    id: 'header',
    name: 'Header Image',
    nameAr: 'صورة الهيدر',
    icon: '🔝',
    isVideo: false,
    description: 'Page or article header',
    descriptionAr: 'هيدر الصفحة أو المقال',
  },
  ad: {
    id: 'ad',
    name: 'Advertisement',
    nameAr: 'إعلان',
    icon: '📢',
    isVideo: false,
    description: 'Advertising content',
    descriptionAr: 'محتوى إعلاني',
  },
  thumbnail: {
    id: 'thumbnail',
    name: 'Thumbnail',
    nameAr: 'صورة مصغرة',
    icon: '🔲',
    isVideo: false,
    description: 'Thumbnail image',
    descriptionAr: 'صورة مصغرة',
  },
  product: {
    id: 'product',
    name: 'Product Image',
    nameAr: 'صورة منتج',
    icon: '🛍️',
    isVideo: false,
    description: 'Product image for e-commerce or catalog',
    descriptionAr: 'صورة منتج للتجارة الإلكترونية أو الكتالوج',
  },
  blog: {
    id: 'blog',
    name: 'Blog Article',
    nameAr: 'مقال مدونة',
    icon: '📝',
    isVideo: false,
    description: 'Blog post or article content',
    descriptionAr: 'مقال أو منشور مدونة',
  },
};

// =====================================================
// SPECIFICATIONS - المواصفات لكل منصة ونوع
// =====================================================
export const SPECIFICATIONS = {
  // Instagram
  instagram: {
    image_post: {
      aspectRatios: [
        { id: '1:1', name: 'Square (1:1)', nameAr: 'مربع', width: 1080, height: 1080, recommended: true },
        { id: '4:5', name: 'Portrait (4:5)', nameAr: 'عمودي', width: 1080, height: 1350, recommended: true },
        { id: '1.91:1', name: 'Landscape (1.91:1)', nameAr: 'أفقي', width: 1080, height: 566 },
      ],
      maxFileSize: 30, // MB
      formats: ['jpg', 'png'],
      captionMaxLength: 2200,
      hashtagsLimit: 30,
    },
    reel: {
      aspectRatios: [
        { id: '9:16', name: 'Vertical (9:16)', nameAr: 'عمودي', width: 1080, height: 1920, recommended: true },
      ],
      minDuration: 3,
      maxDuration: 90,
      maxFileSize: 100, // MB
      formats: ['mp4', 'mov'],
      captionMaxLength: 2200,
      hashtagsLimit: 30,
    },
    story: {
      aspectRatios: [
        { id: '9:16', name: 'Vertical (9:16)', nameAr: 'عمودي', width: 1080, height: 1920, recommended: true },
      ],
      maxDuration: 15,
      maxFileSize: 30,
      formats: ['jpg', 'png', 'mp4', 'mov'],
    },
    carousel: {
      aspectRatios: [
        { id: '1:1', name: 'Square (1:1)', nameAr: 'مربع', width: 1080, height: 1080, recommended: true },
        { id: '4:5', name: 'Portrait (4:5)', nameAr: 'عمودي', width: 1080, height: 1350 },
      ],
      minSlides: 2,
      maxSlides: 10,
      maxFileSize: 30,
      formats: ['jpg', 'png', 'mp4', 'mov'],
      captionMaxLength: 2200,
    },
  },
  
  // TikTok
  tiktok: {
    video_post: {
      aspectRatios: [
        { id: '9:16', name: 'Vertical (9:16)', nameAr: 'عمودي', width: 1080, height: 1920, recommended: true },
      ],
      minDuration: 5,
      maxDuration: 600, // 10 minutes
      maxFileSize: 287, // MB
      formats: ['mp4', 'mov', 'webm'],
      captionMaxLength: 2200,
      hashtagsLimit: 100,
    },
    reel: {
      aspectRatios: [
        { id: '9:16', name: 'Vertical (9:16)', nameAr: 'عمودي', width: 1080, height: 1920, recommended: true },
      ],
      minDuration: 15,
      maxDuration: 60,
      maxFileSize: 287,
      formats: ['mp4', 'mov'],
    },
  },
  
  // Snapchat
  snapchat: {
    video_post: {
      aspectRatios: [
        { id: '9:16', name: 'Vertical (9:16)', nameAr: 'عمودي', width: 1080, height: 1920, recommended: true },
      ],
      minDuration: 3,
      maxDuration: 60,
      maxFileSize: 32, // MB
      formats: ['mp4', 'mov'],
    },
    story: {
      aspectRatios: [
        { id: '9:16', name: 'Vertical (9:16)', nameAr: 'عمودي', width: 1080, height: 1920, recommended: true },
      ],
      maxDuration: 10,
      maxFileSize: 32,
      formats: ['jpg', 'png', 'mp4', 'mov'],
    },
  },
  
  // Twitter/X
  twitter: {
    image_post: {
      aspectRatios: [
        { id: '16:9', name: 'Landscape (16:9)', nameAr: 'أفقي', width: 1200, height: 675, recommended: true },
        { id: '1:1', name: 'Square (1:1)', nameAr: 'مربع', width: 1200, height: 1200 },
      ],
      maxFileSize: 5, // MB
      formats: ['jpg', 'png', 'gif'],
      captionMaxLength: 280,
    },
    video_post: {
      aspectRatios: [
        { id: '16:9', name: 'Landscape (16:9)', nameAr: 'أفقي', width: 1280, height: 720, recommended: true },
        { id: '1:1', name: 'Square (1:1)', nameAr: 'مربع', width: 720, height: 720 },
      ],
      minDuration: 0.5,
      maxDuration: 140,
      maxFileSize: 512,
      formats: ['mp4', 'mov'],
    },
    header: {
      aspectRatios: [
        { id: '3:1', name: 'Header (3:1)', nameAr: 'هيدر', width: 1500, height: 500, recommended: true },
      ],
      maxFileSize: 5,
      formats: ['jpg', 'png'],
    },
  },
  
  // Facebook
  facebook: {
    image_post: {
      aspectRatios: [
        { id: '1.91:1', name: 'Landscape (1.91:1)', nameAr: 'أفقي', width: 1200, height: 628, recommended: true },
        { id: '1:1', name: 'Square (1:1)', nameAr: 'مربع', width: 1080, height: 1080 },
        { id: '4:5', name: 'Portrait (4:5)', nameAr: 'عمودي', width: 1080, height: 1350 },
      ],
      maxFileSize: 30,
      formats: ['jpg', 'png'],
      captionMaxLength: 63206,
    },
    video_post: {
      aspectRatios: [
        { id: '16:9', name: 'Landscape (16:9)', nameAr: 'أفقي', width: 1280, height: 720, recommended: true },
        { id: '1:1', name: 'Square (1:1)', nameAr: 'مربع', width: 1080, height: 1080 },
        { id: '9:16', name: 'Vertical (9:16)', nameAr: 'عمودي', width: 1080, height: 1920 },
      ],
      maxDuration: 14400, // 240 minutes
      maxFileSize: 4000, // 4GB
      formats: ['mp4', 'mov'],
    },
    story: {
      aspectRatios: [
        { id: '9:16', name: 'Vertical (9:16)', nameAr: 'عمودي', width: 1080, height: 1920, recommended: true },
      ],
      maxDuration: 20,
      maxFileSize: 100,
      formats: ['jpg', 'png', 'mp4', 'mov'],
    },
    cover: {
      aspectRatios: [
        { id: '2.7:1', name: 'Cover (2.7:1)', nameAr: 'غلاف', width: 820, height: 312, recommended: true },
      ],
      maxFileSize: 100,
      formats: ['jpg', 'png'],
    },
    carousel: {
      aspectRatios: [
        { id: '1:1', name: 'Square (1:1)', nameAr: 'مربع', width: 1080, height: 1080, recommended: true },
      ],
      minSlides: 2,
      maxSlides: 10,
      maxFileSize: 30,
      formats: ['jpg', 'png', 'mp4', 'mov'],
    },
  },
  
  // LinkedIn
  linkedin: {
    image_post: {
      aspectRatios: [
        { id: '1.91:1', name: 'Landscape (1.91:1)', nameAr: 'أفقي', width: 1200, height: 628, recommended: true },
        { id: '1:1', name: 'Square (1:1)', nameAr: 'مربع', width: 1080, height: 1080 },
      ],
      maxFileSize: 8,
      formats: ['jpg', 'png'],
      captionMaxLength: 3000,
    },
    video_post: {
      aspectRatios: [
        { id: '16:9', name: 'Landscape (16:9)', nameAr: 'أفقي', width: 1920, height: 1080, recommended: true },
        { id: '1:1', name: 'Square (1:1)', nameAr: 'مربع', width: 1080, height: 1080 },
      ],
      minDuration: 3,
      maxDuration: 600, // 10 minutes
      maxFileSize: 200,
      formats: ['mp4'],
    },
    banner: {
      aspectRatios: [
        { id: '4:1', name: 'Banner (4:1)', nameAr: 'بانر', width: 1584, height: 396, recommended: true },
      ],
      maxFileSize: 8,
      formats: ['jpg', 'png'],
    },
    carousel: {
      aspectRatios: [
        { id: '1:1', name: 'Square (1:1)', nameAr: 'مربع', width: 1080, height: 1080, recommended: true },
        { id: '1.91:1', name: 'Landscape (1.91:1)', nameAr: 'أفقي', width: 1200, height: 628 },
      ],
      minSlides: 2,
      maxSlides: 20,
      maxFileSize: 8,
      formats: ['jpg', 'png', 'pdf'],
    },
  },
  
  // Website (includes Blog content types)
  website: {
    banner: {
      aspectRatios: [
        { id: '3:1', name: 'Wide Banner (3:1)', nameAr: 'بانر عريض', width: 1200, height: 400 },
        { id: '4:1', name: 'Full Width (4:1)', nameAr: 'عرض كامل', width: 1600, height: 400, recommended: true },
        { id: '2:1', name: 'Article Banner (2:1)', nameAr: 'بانر مقال', width: 1200, height: 600 },
        { id: 'custom', name: 'Custom', nameAr: 'مخصص', width: null, height: null },
      ],
      maxFileSize: 5,
      formats: ['jpg', 'png', 'webp'],
    },
    hero: {
      aspectRatios: [
        { id: '16:9', name: 'Widescreen (16:9)', nameAr: 'شاشة عريضة', width: 1920, height: 1080, recommended: true },
        { id: '21:9', name: 'Ultra Wide (21:9)', nameAr: 'فائق العرض', width: 2560, height: 1080 },
        { id: 'custom', name: 'Custom', nameAr: 'مخصص', width: null, height: null },
      ],
      maxFileSize: 10,
      formats: ['jpg', 'png', 'webp'],
    },
    thumbnail: {
      aspectRatios: [
        { id: '16:9', name: 'Landscape (16:9)', nameAr: 'أفقي', width: 640, height: 360, recommended: true },
        { id: '1:1', name: 'Square (1:1)', nameAr: 'مربع', width: 400, height: 400 },
      ],
      maxFileSize: 2,
      formats: ['jpg', 'png', 'webp'],
    },
    header: {
      aspectRatios: [
        { id: '3:1', name: 'Header (3:1)', nameAr: 'هيدر', width: 1500, height: 500, recommended: true },
        { id: '16:9', name: 'Widescreen (16:9)', nameAr: 'شاشة عريضة', width: 1280, height: 720 },
      ],
      maxFileSize: 5,
      formats: ['jpg', 'png', 'webp'],
    },
    product: {
      aspectRatios: [
        { id: '1:1', name: 'Square (1:1)', nameAr: 'مربع', width: 1200, height: 1200, recommended: true },
        { id: '4:5', name: 'Portrait (4:5)', nameAr: 'عمودي', width: 1200, height: 1500 },
        { id: '16:9', name: 'Landscape (16:9)', nameAr: 'أفقي', width: 1920, height: 1080 },
        { id: 'custom', name: 'Custom', nameAr: 'مخصص', width: null, height: null },
      ],
      maxFileSize: 10,
      formats: ['jpg', 'png', 'webp'],
      description: 'Product images for e-commerce platforms and catalogs',
      descriptionAr: 'صور المنتجات للمتاجر الإلكترونية والكتالوجات',
    },
    blog: {
      aspectRatios: [
        { id: '2:1', name: 'Article Banner (2:1)', nameAr: 'بانر مقال', width: 1200, height: 600, recommended: true },
        { id: '16:9', name: 'Widescreen (16:9)', nameAr: 'شاشة عريضة', width: 1280, height: 720 },
        { id: '4:3', name: 'Standard (4:3)', nameAr: 'قياسي', width: 1200, height: 900 },
        { id: 'custom', name: 'Custom', nameAr: 'مخصص', width: null, height: null },
      ],
      maxFileSize: 5,
      formats: ['jpg', 'png', 'webp'],
      description: 'Blog post featured image or article header',
      descriptionAr: 'صورة مميزة لمقال المدونة أو هيدر المقال',
    },
  },
  
  // Other - مخصص
  other: {
    image_post: {
      aspectRatios: [
        { id: '1:1', name: 'Square (1:1)', nameAr: 'مربع', width: 1080, height: 1080 },
        { id: '16:9', name: 'Landscape (16:9)', nameAr: 'أفقي', width: 1920, height: 1080 },
        { id: '9:16', name: 'Portrait (9:16)', nameAr: 'عمودي', width: 1080, height: 1920 },
        { id: 'custom', name: 'Custom', nameAr: 'مخصص', width: null, height: null },
      ],
      maxFileSize: 50,
      formats: ['jpg', 'png', 'webp', 'gif'],
    },
    video_post: {
      aspectRatios: [
        { id: '16:9', name: 'Landscape (16:9)', nameAr: 'أفقي', width: 1920, height: 1080 },
        { id: '9:16', name: 'Portrait (9:16)', nameAr: 'عمودي', width: 1080, height: 1920 },
        { id: '1:1', name: 'Square (1:1)', nameAr: 'مربع', width: 1080, height: 1080 },
        { id: 'custom', name: 'Custom', nameAr: 'مخصص', width: null, height: null },
      ],
      maxDuration: 3600,
      maxFileSize: 500,
      formats: ['mp4', 'mov', 'webm'],
    },
    banner: {
      aspectRatios: [
        { id: 'custom', name: 'Custom', nameAr: 'مخصص', width: null, height: null },
      ],
      maxFileSize: 10,
      formats: ['jpg', 'png', 'webp'],
    },
  },
};

// =====================================================
// HELPER FUNCTIONS
// =====================================================

/**
 * Get content types for a specific platform
 */
export const getContentTypesForPlatform = (platformId) => {
  const platform = PLATFORMS[platformId];
  if (!platform) return [];
  
  return platform.contentTypes.map(typeId => CONTENT_TYPES[typeId]).filter(Boolean);
};

/**
 * Get specifications for a platform and content type
 */
export const getSpecifications = (platformId, contentTypeId) => {
  return SPECIFICATIONS[platformId]?.[contentTypeId] || null;
};

/**
 * Get default specifications for a platform and content type
 */
export const getDefaultSpecifications = (platformId, contentTypeId) => {
  const specs = getSpecifications(platformId, contentTypeId);
  if (!specs) return {};
  
  const defaultAspectRatio = specs.aspectRatios?.find(ar => ar.recommended) || specs.aspectRatios?.[0];
  
  return {
    aspect_ratio: defaultAspectRatio?.id || '1:1',
    width: defaultAspectRatio?.width || 1080,
    height: defaultAspectRatio?.height || 1080,
    duration_seconds: specs.minDuration || null,
    max_duration: specs.maxDuration || null,
    min_duration: specs.minDuration || null,
    slides_count: specs.minSlides || null,
    max_slides: specs.maxSlides || null,
    max_file_size_mb: specs.maxFileSize || 50,
    formats: specs.formats || ['jpg', 'png'],
    caption_max_length: specs.captionMaxLength || 2200,
    hashtags_limit: specs.hashtagsLimit || 30,
  };
};

/**
 * Check if content type is video
 */
export const isVideoContent = (contentTypeId) => {
  return CONTENT_TYPES[contentTypeId]?.isVideo || false;
};

/**
 * Format duration (seconds to mm:ss)
 */
export const formatDuration = (seconds) => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export default {
  PLATFORMS,
  CONTENT_TYPES,
  SPECIFICATIONS,
  getContentTypesForPlatform,
  getSpecifications,
  getDefaultSpecifications,
  isVideoContent,
  formatDuration,
};





